import { useAppContext } from "../AppProviders";

function validatePermissionKey(key: string, permissions?: string[]): boolean {
  if (!permissions) return false;
  return permissions.some((permission) => permission === key);
}

function usePermissionValidator() {
  const { permissions } = useAppContext();

  const validateFunctor = (key: string) =>
    validatePermissionKey(key, permissions);

  return {
    canAddBlueprints: validateFunctor("blueprint:new"),
    canAddReport: validateFunctor("report:add"),
    canRerunReport: validateFunctor("report:rerun"),
    canPostScrape: validateFunctor("report:post_scrape"),
    canPostRate: validateFunctor("report:post_rate"),
    canConfirmProfile: validateFunctor("report:profile_confirm"),
    canAddNotes: validateFunctor("report:note"),
    canComplete: validateFunctor("report:completed"),
    canAmend: validateFunctor("report:open_amendment"),
    canInProcess: validateFunctor("report:in_process"),
    canClientInProgress: validateFunctor("report:client_in_progress"),
    canInternalQaView: validateFunctor("report:qa_view"),
    canInternalQaEdit: validateFunctor("report:qa_edit"),
    canClientQaView: validateFunctor("report:client_qa_view"),
    canClientQaEdit: validateFunctor("report:client_qa_edit"),
    canNavPendingReview: validateFunctor("report:pending_review"),
    canNavLegacyCore: validateFunctor("core:legacy_report_view"),
    canNavReviewed: validateFunctor("report:reviewed"),
    canNavAmendment: validateFunctor("report:amendment"),
    canUnlock: validateFunctor("report:unlock"),
    canCancelMisconduct: validateFunctor("report:cancel"),
    canChangeDueDate: validateFunctor("report:due_date"),
    canManageReports: validateFunctor("report:page"),
    canRegeneratePDF: validateFunctor("report:regenerate_pdf"),
    canDownloadPDF: validateFunctor("report:can_download_pdf"),
    canBulkUploadReports: validateFunctor("report:bulk_upload"),
    canResumeWorkflow: validateFunctor("report:resume_workflow"),
    canViewAllReportsWithinCompany: validateFunctor(
      "report:view_all_reports_within_company"
    ),
    canDeleteReportReviewedTab: validateFunctor("report:review_tab_delete"),
    canDeleteClientAddedProfile: validateFunctor(
      "report:delete_confirmed_profile"
    ), // badly named permission, can delete any kind of profile
    canPrintCurrentView: validateFunctor("report:print_current_view"),
    canManageBlueprints: validateFunctor("blueprint:page"),
    canManageCompanies: validateFunctor("company:page"),
    canManageConsent: validateFunctor("consent:page"),
    canResendConsentEmail: validateFunctor("consent:resend_email"),
    companies: {
      canAdd: validateFunctor("company:add_company"),
      canAddSubcompany: validateFunctor("companies:add_subcompany"),
      canViewDetails: validateFunctor("companies:view"),
      canEditDetails: validateFunctor("companies:edit"),
      canViewBlueprints: validateFunctor("companies:view_blueprint"),
      canEditBlueprints: validateFunctor("companies:edit_blueprint"),
      canViewBehaviorKits: validateFunctor("companies:view_behavior_kit"),
      canEditBehaviorKits: validateFunctor("companies:edit_behavior_kit"),
      canViewNotes: validateFunctor("companies:notes"),
      canViewApiKeys: validateFunctor("api_key:read"),
      canCreateApiKeys: validateFunctor("api_key:create"),
      canDeleteApiKeys: validateFunctor("api_key:delete"),
      canViewSettings: validateFunctor("company:view_settings"),
      canEditCompanySetting: validateFunctor("companies:client_setting_edit"),
      canEditCompanyPdfTemplate: validateFunctor("company:edit_pdf_template"),
    },
    canManageWorkflows: validateFunctor("workflow:page"),
    canManageAnalystRoles: validateFunctor("workflow:analyst_assign"),
    canManageThresholds: validateFunctor("workflow:thresholds"),
    canManageUsers: validateFunctor("user:page"),
    subcompanies: {
      canViewDetails: validateFunctor("subcompanies:view"),
      canEditDetails: validateFunctor("subcompanies:edit"),
      canViewBlueprints: validateFunctor("subcompanies:view_blueprint"),
      canEditBlueprints: validateFunctor("subcompanies:edit_blueprint"),
      canViewBehaviorKits: validateFunctor("subcompanies:view_behavior_kit"),
      canEditBehaviorKits: validateFunctor("subcompanies:edit_behavior_kit"),
      canViewNotes: validateFunctor("subcompanies:notes"),
      canViewSettings: validateFunctor("subcompanies:view_settings"),
      canEditSubcompanySetting: validateFunctor(
        "subcompanies:client_sub_setting_edit"
      ),
    },
    canManageATS: validateFunctor("ats:page"),
    canSelfManageATS: validateFunctor("ats:self_manage"),
    canManageAdminView: validateFunctor("admin:fama_admin_view"),
    canManageAdminCompleted: validateFunctor("admin:customer_support_view"),
    canViewUsageReport: validateFunctor("thought_spot:usage_report_edit"),
    canDownloadUsageReport: validateFunctor(
      "thought_spot:usage_report_download"
    ),
    canView360: validateFunctor("360:view"),
    canEditAmendment360: validateFunctor("360:amendment_edit"),
    canEdit360Reports: validateFunctor("360:report_edit"),
    canView360Qa: validateFunctor("360:qa_view"),
    canEdit360Qa: validateFunctor("360:qa_edit"),
    canCancel360: validateFunctor("360:cancel"),
    canEdit360DueDate: validateFunctor("360:edit_due_date"),
    canViewWIP360Reports: validateFunctor("360:wip_view"),
    person: {
      canCreateRecur: validateFunctor("person:create_recurrence"),
      canCancelRecur: validateFunctor("person:cancel_recurrence"),
      canEditRecur: validateFunctor("person:edit_recurrence"),
    },
    legacy: {
      canGetUsers: validateFunctor("legacy:get_users"),
      canCreateUsers: validateFunctor("legacy:create_users"),
    },
  };
}

export { usePermissionValidator };
